import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformServer } from '@angular/common';
import { postHogHelper } from '../helpers/posthog.helpers';
import {
  IAddPaymentInfoPayload,
  IAddShippingInfoPayload,
  IAddToCartPayload,
  IAuthTrackPayload,
  IVirtualPageViewPayload
} from '../interfaces';
import { IOrder, IProductType } from '@cakeiteasy/web-utils';
import { PostHogManagerService } from './posthog-manager.service';
import { ICart } from '../../cart/models/cart/i-cart';
import { ICartLine } from '../../cart/models/cart/i-cart-line';

@Injectable({
  providedIn: 'root'
})
export class PostHogTrackerService {
  constructor(@Inject(PLATFORM_ID) protected _platformId: any,
    private postHogManager: PostHogManagerService) {
  }

  async trackRemoveFromCart(cart: ICart, lines: ICartLine[]): Promise<void> {
    await this.capture(...postHogHelper.getRemoveFromCartTrack(cart, lines));
  }

  async trackViewCart(cart: ICart): Promise<void> {
    await this.capture(...postHogHelper.getViewCartTrack(cart));
  }

  async trackSearch(data: { searchTerm: string, pageLocation: string }): Promise<void> {
    await this.capture(...postHogHelper.getSearchTrack(data));
  }

  async trackSearchNoResult(data: { searchTerm: string, pageLocation: string }): Promise<void> {
    await this.capture(...postHogHelper.trackSearchNoResult(data));
  }

  async trackSignUp(payload: IAuthTrackPayload): Promise<void> {
    this.postHogManager.identifyAuthPayload(payload, { signup_date: new Date().toISOString() });
    await this.capture(...postHogHelper.getSignUpTrack(payload));
  }

  async trackLogin(payload: IAuthTrackPayload): Promise<void> {
    this.postHogManager.identifyAuthPayload(payload, { last_login: new Date().toISOString() });
    await this.capture(...postHogHelper.getLoginTrack(payload));
  }

  async trackLogout(): Promise<void> {
    await this.capture(...postHogHelper.getLogoutTrack());
  }

  async trackVirtualPageView(payload: IVirtualPageViewPayload): Promise<void> {
    await this.capture(...postHogHelper.getVirtualPageViewTrack(payload));
  }

  async trackAddToCart(payload: IAddToCartPayload): Promise<void> {
    await this.capture(...postHogHelper.getAddToCartTrack(payload));
  }

  async trackViewItem(product: IProductType): Promise<void> {
    await this.capture(...postHogHelper.getViewItemTrack(product));
  }

  async trackAddShippingInfo(params: IAddShippingInfoPayload): Promise<void> {
    await this.capture(...postHogHelper.getAddShippingInfoTrack(params));
  }

  async trackBeginCheckout(params: IAddShippingInfoPayload): Promise<void> {
    await this.capture(...postHogHelper.getBeginCheckoutTrack(params));
  }

  async trackAddPaymentInfo(payload: IAddPaymentInfoPayload): Promise<void> {
    this.postHogManager.identifyPayment(payload.userData);
    await this.capture(...postHogHelper.getPaymentTrack(payload));
  }

  async trackOrderPurchase(order: IOrder): Promise<void> {
    this.postHogManager.identifyOrderPurchase(order);
    await this.capture(...postHogHelper.getOrderPurchaseTrack(order));
  }

  protected async capture(event: string,
    data: { [key: string]: number | string | boolean | object }): Promise<void> {
    if (isPlatformServer(this._platformId)) {
      return;
    }

    try {
      await this.postHogManager.capture(event, data);
    } catch (error) {
      console.error('PostHog Error: ', error);
    }
  }
}
